/* CI024 - Charles & Ivy - Register a collection filter tool
 * ---------------------------------------------------------------------------
 * Exposes a read-only `filter_collection` tool to an in-browser AI agent via
 * document.modelContext. Calls the same Boost endpoint and the same parameters
 * the storefront already sends (verified against a live network request).
 *
 * SAFETY
 *  - Does nothing at all unless document.modelContext exists (normal shoppers
 *    exit in microseconds).
 *  - Never touches the DOM, never defines a global, never patches anything
 *    Boost owns. Read-only GETs only.
 *  - Whole body wrapped in try/catch, so any unexpected error here cannot
 *    surface as a page error or affect other scripts.
 *  - Feature-guarded: bails cleanly on browsers without fetch/URLSearchParams.
 *
 * REQUIRES boostPFSConfig, defined inline by snippets/boost-pfs.liquid.
 * Must therefore be loaded AFTER {% render 'boost-pfs' %}. Use defer.
 */

(function () {
  'use strict';

  try {
    // ---- Feature guards ---------------------------------------------------
    // If any of these are missing we are on a browser that cannot run the
    // tool. Exit silently rather than registering something that will throw.
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    if (typeof window.fetch !== 'function') return;
    if (typeof window.URLSearchParams !== 'function') return;
    if (typeof window.Promise !== 'function') return;

    var REQUEST_TIMEOUT_MS = 8000;
    var FALLBACK_ENDPOINT = 'https://services.mybcapps.com/bc-sf-filter/filter';
    var FALLBACK_SHOP = 'charlesandivy.myshopify.com';

    // ---- Config access ----------------------------------------------------
    // Boost merges boostPFSAppConfig + boostPFSThemeConfig into boostPFSConfig.
    // Prefer the merged object; fall back to the app config if the merge has
    // not run. Every read is defensive: a missing branch returns null, never
    // throws.
    function cfg() {
      return window.boostPFSConfig || window.boostPFSAppConfig || null;
    }

    function branch(name, key) {
      var c = cfg();
      return (c && c[name] && c[name][key]) || null;
    }

    function collectionId() {
      // boost-pfs.liquid emits 0 on non-collection pages.
      var id = branch('general', 'collection_id');
      return id ? String(id) : null;
    }

    function endpoint() {
      return branch('api', 'filterUrl') || FALLBACK_ENDPOINT;
    }

    function shopDomain() {
      return branch('shop', 'domain') || FALLBACK_SHOP;
    }

    // ---- Request building -------------------------------------------------
    function buildParams(input, scope) {
      var p = new URLSearchParams({
        _: 'pf',
        shop: shopDomain(),
        page: '1',
        limit: String(Math.min(Math.max(parseInt(input.limit, 10) || 12, 1), 24)),
        sort: input.sort || branch('general', 'default_sort_by') || 'manual',
        collection_scope: scope,
        tag: '',
        product_available: 'true',
        variant_available: 'true',
        build_filter_tree: 'false',
        check_cache: 'false',
        locale: branch('general', 'current_locale') || 'en',
        event_type: 'filter'
      });
      var facets = Array.isArray(input.facets) ? input.facets : [];
      facets.forEach(function (f) {
        if (!f || typeof f.id !== 'string' || typeof f.value !== 'string') return;
        p.append(f.id + '[]', f.value); // brackets are required by Boost
      });
      return p;
    }

    // Aborts rather than hanging forever if Boost is slow or unreachable.
    function getJSON(url) {
      var controller = null;
      var timer = null;
      var opts = { credentials: 'omit' };

      if (typeof AbortController === 'function') {
        controller = new AbortController();
        opts.signal = controller.signal;
        timer = setTimeout(function () { controller.abort(); }, REQUEST_TIMEOUT_MS);
      }

      return fetch(url, opts).then(function (res) {
        if (timer) clearTimeout(timer);
        if (!res.ok) throw new Error('Boost returned HTTP ' + res.status);
        return res.json();
      }, function (err) {
        if (timer) clearTimeout(timer);
        throw err;
      });
    }

    // ---- Facet resolution -------------------------------------------------
    // Boost matches facet values exactly, so "slat-type_hudson" returns zero
    // results where "slat-type_Hudson" returns products. The agent cannot be
    // relied on to reproduce casing, and casing is inconsistent across facets
    // anyway (post-type_aluminium is lower case, slat-colour_Dark Grey is not),
    // so resolve what the agent sends against the collection's real facet tree
    // before querying.

    var treeCache = {}; // scope|locale -> promise of the parsed filter tree

    function facetTree(scope) {
      var key = scope + '|' + (branch('general', 'current_locale') || 'en');
      if (!treeCache[key]) {
        var p = buildParams({}, scope);
        p.set('build_filter_tree', 'true');
        p.set('limit', '1');
        treeCache[key] = getJSON(endpoint() + '?' + p.toString())
          .then(function (j) {
            return (j && j.filter && j.filter.options) || [];
          })
          .catch(function (err) {
            delete treeCache[key]; // do not cache a failure
            throw err;
          });
      }
      return treeCache[key];
    }

    function lower(s) { return String(s === undefined || s === null ? '' : s).toLowerCase().trim(); }

    // Build a lookup of the collection's real ids and values, keyed on
    // lower case, so any casing the agent sends resolves to the canonical key.
    function buildIndex(options) {
      var byId = {};
      options.forEach(function (o) {
        if (!o || !o.filterOptionId) return;
        var values = {};
        (o.values || []).forEach(function (v) {
          if (!v || typeof v.key !== 'string') return;
          values[lower(v.key)] = v.key;
          // Values are "prefix_Label"; also accept the label on its own, so
          // "Hudson" resolves as readily as "slat-type_Hudson".
          var us = v.key.indexOf('_');
          if (us > -1) {
            var bare = lower(v.key.slice(us + 1));
            if (bare && !values[bare]) values[bare] = v.key;
          }
        });
        byId[lower(o.filterOptionId)] = {
          id: o.filterOptionId,
          label: o.label,
          status: o.status,
          values: values,
          keys: (o.values || []).map(function (v) { return v.key; })
        };
      });
      return byId;
    }

    // Resolves to { facets: [...canonical...], unmatched: [...] }
    function resolveFacets(scope, facets) {
      var supplied = Array.isArray(facets) ? facets.filter(function (f) {
        return f && typeof f.id === 'string' && typeof f.value === 'string';
      }) : [];
      if (!supplied.length) return Promise.resolve({ facets: [], unmatched: [] });

      return facetTree(scope).then(function (options) {
        var index = buildIndex(options);
        var out = [];
        var unmatched = [];

        supplied.forEach(function (f) {
          var opt = index[lower(f.id)];
          if (!opt) {
            unmatched.push({
              id: f.id,
              value: f.value,
              reason: 'That filter does not exist in this collection.'
            });
            return;
          }
          var canonical = opt.values[lower(f.value)];
          if (!canonical) {
            unmatched.push({
              id: opt.id,
              value: f.value,
              reason: 'That value is not available in this collection.',
              available: opt.keys
            });
            return;
          }
          out.push({ id: opt.id, value: canonical });
        });

        return { facets: out, unmatched: unmatched };
      });
    }

    function unmatchedError(unmatched) {
      return {
        error: 'unknown_facet',
        unmatched: unmatched,
        note: 'One or more filters were not recognised in this collection, ' +
          'so no query was run rather than returning results that ignore ' +
          'them. Use the values listed, or call list_facets to see what this ' +
          'collection offers. Do not tell the customer a product is ' +
          'unavailable on the basis of this response.'
      };
    }

    function fail(message) {
      return {
        error: 'filter_unavailable',
        note: 'The product filter service did not respond, so no results ' +
          'could be fetched. Read the products shown on the page instead. (' +
          message + ')'
      };
    }

    // ---- Tool definition --------------------------------------------------
    function definition() {
      return {
        name: 'filter_collection',
        description:
          'Narrow the products in the collection currently being viewed by ' +
          'any filter that the collection offers. Filters differ between ' +
          'collections. Read-only. Collections here can hold thousands of ' +
          'products, so always call with list_facets first and use the facet ' +
          'ids and values it returns. Do not construct values yourself. ' +
          'Casing is corrected automatically, so a value that differs only ' +
          'in case will still match.',
        readOnlyHint: true,
        inputSchema: {
          type: 'object',
          properties: {
            facets: {
              type: 'array',
              description: 'Facet selections, using ids and values returned ' +
                'by list_facets for this collection.',
              items: {
                type: 'object',
                properties: {
                  id: {
                    type: 'string',
                    description: 'A facet id from list_facets for this collection.'
                  },
                  value: {
                    type: 'string',
                    description: 'A value key from list_facets for this ' +
                      'collection. Casing is corrected automatically.'
                  }
                },
                required: ['id', 'value']
              }
            },
            collection_id: { type: 'string' },
            sort: {
              type: 'string',
              description: 'Boost sort key, e.g. manual, best-selling, price-ascending.'
            },
            limit: { type: 'integer', minimum: 1, maximum: 24, default: 12 },
            list_facets: {
              type: 'boolean',
              default: false,
              description: 'Return the available facets for the current ' +
                'selection instead of products.'
            }
          }
        },
        execute: function (input) {
          try {
            input = input || {};

            var scope = String(input.collection_id || collectionId() || '');
            if (!scope) {
              return Promise.resolve({
                error: 'no_collection',
                note: 'This tool only works on a collection page. No ' +
                  'collection is in scope, so no filtering was attempted.'
              });
            }

            return resolveFacets(scope, input.facets).then(function (r) {
              if (r.unmatched.length) return unmatchedError(r.unmatched);

              // Query with the canonical values, whatever casing arrived.
              var resolved = {};
              for (var k in input) if (input.hasOwnProperty(k)) resolved[k] = input[k];
              resolved.facets = r.facets;

              if (resolved.list_facets) {
                var fp = buildParams(resolved, scope);
                fp.set('build_filter_tree', 'true');
                fp.set('limit', '1');
                return getJSON(endpoint() + '?' + fp.toString())
                  .then(function (fj) {
                    var options = (fj && fj.filter && fj.filter.options) || [];
                    return {
                      scope_note: r.facets.length
                        ? 'Counts are scoped to the facets you passed in.'
                        : 'Counts cover the whole collection.',
                      facets: options
                        .filter(function (o) {
                          // Drop disabled facets and single-value facets,
                          // which cannot narrow anything.
                          return o && o.status === 'active' &&
                            Array.isArray(o.values) && o.values.length > 1;
                        })
                        .map(function (o) {
                          return {
                            id: o.filterOptionId,
                            label: o.label,
                            values: o.values.map(function (v) {
                              return { value: v.key, count: v.doc_count };
                            })
                          };
                        })
                    };
                  });
              }

              return getJSON(endpoint() + '?' + buildParams(resolved, scope).toString())
                .then(function (j) {
                var handle = branch('general', 'collection_handle') || '';
                var prefix = handle ? '/collections/' + handle : '';
                var raw = (j && Array.isArray(j.products)) ? j.products : [];

                var products = raw
                  .filter(function (p) { return p && p.handle; })
                  .map(function (p) {
                    return {
                      title: p.title,
                      handle: p.handle,
                      url: prefix + '/products/' + p.handle,
                      price_from: p.price_min,
                      price_to: p.price_max,
                      available: p.available
                    };
                  });

                var total = (j && typeof j.total_product === 'number')
                  ? j.total_product : products.length;

                if (!products.length) {
                  return {
                    total: 0,
                    products: [],
                    note: 'No products match that combination. Ask for the ' +
                      'facet list with list_facets and try a wider selection.'
                  };
                }

                var result = {
                  total: total,
                  showing: products.length,
                  currency: branch('shop', 'currency'),
                  products: products
                };

                // A handful of rows out of thousands is not a useful answer.
                if (total > products.length * 4) {
                  result.note = 'Only a small sample of ' + total +
                    ' matches is shown. Add facets from list_facets to ' +
                    'narrow before presenting results.';
                }
                  return result;
                });
            }).catch(function (err) { return fail(err.message); });
          } catch (err) {
            // Never let a synchronous throw escape into the agent runtime.
            return Promise.resolve(fail(err && err.message ? err.message : 'unknown error'));
          }
        }
      };
    }

    // ---- Registration -----------------------------------------------------
    // document.modelContext is injected by the agent runtime. Its timing
    // relative to a deferred script is not guaranteed, so make a small bounded
    // number of attempts (4 over 1.5s) and then give up for good.
    var attempts = 0;

    function tryRegister() {
      try {
        var mc = document.modelContext;
        if (mc && typeof mc.registerTool === 'function') {
          mc.registerTool(definition());
          return;
        }
      } catch (err) {
        return; // registration refused - stop, do not retry
      }
      if (++attempts < 4) setTimeout(tryRegister, 500);
    }

    tryRegister();
  } catch (err) {
    // Swallow everything. This script is an enhancement for agent traffic and
    // must never affect the storefront.
  }
})();